import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-scroll';
import { FaBars, FaTimes } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import ThemeToggle from './ThemeToggle';
import { profile } from '../data/portfolio';
import { useI18n } from '../context/LanguageContext';

const Bar = styled.nav`
  position: fixed; top: 0; left: 0; width: 100%; z-index: 1000;
  padding: 0.9rem 2rem;
  border-bottom: 1px solid transparent;
  transition: background 0.3s ease, border-color 0.3s ease, padding 0.3s ease;
  &.scrolled {
    background: var(--nav-bg);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--border);
    padding: 0.6rem 2rem;
  }
  @media (max-width: 860px) { padding: 0.9rem 1.25rem; &.scrolled { padding: 0.7rem 1.25rem; } }
`;

const BarInner = styled.div`
  width: 100%;
  max-width: 1680px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-heading); font-weight: 700; font-size: 1.12rem; cursor: pointer;
  .mark {
    display: grid; place-items: center; width: 34px; height: 34px; border-radius: 10px;
    background: var(--gradient-accent); color: #fff; font-size: 0.9rem;
    box-shadow: 0 6px 18px -6px rgba(99,102,241,0.7);
  }
`;

const Center = styled.div`
  display: flex; align-items: center; gap: 0.3rem;
  padding: 0.35rem; border-radius: 999px;
  border: 1px solid var(--border); background: var(--surface); backdrop-filter: blur(8px);
  @media (max-width: 860px) {
    position: absolute; top: 100%; left: 0; right: 0;
    flex-direction: column; align-items: stretch; gap: 0.25rem;
    margin: 0.5rem 1rem 0; padding: 0.75rem; border-radius: 18px;
    background: var(--elev-bg); backdrop-filter: blur(16px); box-shadow: var(--card-shadow);
    transform: translateY(-12px); opacity: 0; pointer-events: none;
    transition: opacity 0.25s ease, transform 0.25s ease;
    &.open { transform: translateY(0); opacity: 1; pointer-events: auto; }
  }
`;

const NavLink = styled(Link)`
  position: relative; padding: 0.5rem 1rem; border-radius: 999px;
  font-size: 0.95rem; font-weight: 500; color: var(--text-muted); cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;
  &:hover { color: var(--text); }
  &.active { color: var(--text); background: var(--surface-hover); }
  @media (max-width: 860px) { padding: 0.75rem 1rem; text-align: left; }
`;

const Right = styled.div`
  display: flex; align-items: center; gap: 0.6rem;
`;

const CvBtn = styled.a`
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.55rem 1.05rem; border-radius: 999px;
  background: var(--gradient-accent); color: #fff; font-weight: 600; font-size: 0.9rem;
  box-shadow: 0 8px 22px -10px rgba(99,102,241,0.7);
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
  @media (max-width: 860px) { display: none; }
`;

const LangBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 42px; height: 38px; padding: 0 0.6rem;
  border: 1px solid var(--border); border-radius: 999px;
  background: var(--surface); color: var(--text-muted);
  font-family: var(--font-heading); font-weight: 700; font-size: 0.8rem;
  letter-spacing: 0.08em; cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  &:hover { color: var(--text); border-color: var(--primary-light); transform: translateY(-1px); }
`;

const Burger = styled.button`
  display: none; background: none; border: 1px solid var(--border); border-radius: 10px;
  color: var(--text); font-size: 1.2rem; width: 42px; height: 42px; cursor: pointer;
  align-items: center; justify-content: center;
  @media (max-width: 860px) { display: inline-flex; }
`;

const Progress = styled.div`
  position: fixed; top: 0; left: 0; height: 3px; z-index: 1001;
  background: var(--gradient-accent); transform-origin: left; width: 100%;
  transform: scaleX(${(p) => p.$progress});
  transition: transform 0.1s linear;
`;

const sections = ['hero', 'about', 'skills', 'projects', 'experience', 'contact'];

const Navbar = () => {
  const { lang, toggle: toggleLang, t } = useI18n();
  const [active, setActive] = useState('hero');
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docH > 0 ? window.scrollY / docH : 0);
      let current = 'hero';
      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.35) current = id;
      });
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Progress $progress={progress} />
      <Bar className={scrolled ? 'scrolled' : ''}>
        <BarInner>
        <Logo to="hero" smooth duration={500} offset={-70}>
          <span className="mark">{profile.initials}</span>
          <span>{profile.shortName}</span>
        </Logo>

        <Center className={open ? 'open' : ''}>
          {sections.map((id) => (
            <NavLink
              key={id} to={id} smooth duration={500} offset={-70}
              className={active === id ? 'active' : ''}
              onClick={() => setOpen(false)}
            >
              {t.nav[id]}
            </NavLink>
          ))}
        </Center>

        <Right>
          <LangBtn onClick={toggleLang} aria-label="Changer de langue / switch language">
            {lang === 'en' ? 'FR' : 'EN'}
          </LangBtn>
          <ThemeToggle />
          <CvBtn href={`${process.env.PUBLIC_URL}/${profile.cv}`} download>
            <FiDownload /> {t.nav.cv}
          </CvBtn>
          <Burger onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <FaTimes /> : <FaBars />}
          </Burger>
        </Right>
        </BarInner>
      </Bar>
    </>
  );
};

export default Navbar;
